import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  alpha,
  useTheme,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  Stack,
} from '@mui/material';
import { TrendingUp as TrendingUpIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { PriceHistoryChart } from '../PriceHistoryChart';
import { JustTCGCardData } from '../../services/justTcgService';
import { useMarketPricing, useJustTCGv2Pricing } from '../../hooks/useMarketPricing';
import EbayMarketplace from '../EbayMarketplace';

// Alias the data type for future generic usage
export type MarketPricingData = JustTCGCardData;

interface MarketPricingProps {
  cardNumber: string;
  name: string;
  setId: string;
  rarity?: string;
  skip?: boolean;
  searchQuery?: string; // Optional: Override default search format (for Pokemon)
  justtcgId?: string; // Optional: Direct JustTCG ID
  tcgplayerUrl?: string; // Optional: Exact TCGPlayer URL from card database
  game?: 'pokemon' | 'onepiece'; // Optional: Explicit game type
  cache?: string; // Optional: cache bypass flag ('false')
}

const MarketPricing: React.FC<MarketPricingProps> = ({
  cardNumber,
  name,
  setId,
  rarity,
  skip = false,
  searchQuery,
  justtcgId,
  tcgplayerUrl: externalTcgplayerUrl,
  game: explicitGame,
  cache,
}) => {
  const theme = useTheme();
  const [marketTab, setMarketTab] = React.useState(0);
  const [pricingSource, setPricingSource] = React.useState<'tcgdex' | 'justtcg'>('tcgdex');

  // Detect game type
  const game = explicitGame || (setId.startsWith('OP') || setId.startsWith('ST') || setId.startsWith('EB') || setId.startsWith('PRB') ? 'onepiece' : 'pokemon');

  // Encapsulated data fetching
  const {
    data: pricingData,
    isLoading: loading,
    error,
  } = useMarketPricing(cardNumber, name, setId, rarity, !skip, searchQuery, justtcgId);

  const {
    data: justTcgData,
    isLoading: loadingV2,
  } = useJustTCGv2Pricing(
    cardNumber,
    name,
    setId,
    game === 'pokemon' && !skip,
    searchQuery,
    justtcgId,
    cache
  );

  // Dynamically resolve active pricing data based on selected source
  const activePricing = (game === 'pokemon' && pricingSource === 'justtcg' && justTcgData) 
    ? justTcgData 
    : (pricingData || justTcgData);

  // Clean name for eBay
  const cleanName = name.replace(/\./g, ' ').replace(/\s+/g, ' ').trim();
  
  const buildDefaultQuery = () => {
    if (game === 'onepiece') {
      return `${cleanName} ${cardNumber} One Piece`;
    }
    return `${cleanName} ${cardNumber}`;
  };

  const ebaySearchQuery = searchQuery || buildDefaultQuery();
  
  const tcgplayerUrl = externalTcgplayerUrl || (
    game === 'pokemon' 
      ? `https://www.tcgplayer.com/search/all/product?q=${encodeURIComponent(name)}+${encodeURIComponent(cardNumber)}`
      : `https://www.tcgplayer.com/search/all/product?q=${encodeURIComponent(name)}+${encodeURIComponent(cardNumber)}+${encodeURIComponent(setId)}`
  );

  const priceChartUrl = `https://www.pricecharting.com/search-products?q=${encodeURIComponent(name)}+${encodeURIComponent(cardNumber)}+${encodeURIComponent(setId)}&type=prices`;

  // Helpers
  const formatChange = (val: number | null | undefined) => {
    if (val == null) return null;
    const prefix = val >= 0 ? '+' : '';
    const color = val >= 0 ? theme.palette.success.main : theme.palette.error.main;
    return { text: `${prefix}${val.toFixed(1)}%`, color };
  };

  const marketTabs = ['eBay', 'TCGPlayer', 'PriceCharting'];

  return (
    <Box>
      {/* Section header */}
      <Typography
        variant="subtitle2"
        sx={{ 
          color: 'text.secondary', 
          textTransform: 'uppercase', 
          letterSpacing: '0.05em',
          fontSize: '0.7rem',
          mb: 2,
        }}
      >
        Market Pricing
      </Typography>

      {/* Pricing Source Toggle Selector */}
      {game === 'pokemon' && justTcgData && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 2.5, 
          p: 0.5,
          borderRadius: '8px',
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.text.primary, 0.04) : alpha(theme.palette.text.primary, 0.02),
          border: `1px solid ${theme.palette.divider}`,
        }}>
          <Button
            size="small"
            fullWidth
            onClick={() => setPricingSource('tcgdex')}
            disabled={!pricingData}
            sx={{
              py: 0.75,
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '6px',
              textTransform: 'none',
              bgcolor: pricingSource === 'tcgdex' ? (theme.palette.mode === 'dark' ? 'primary.main' : 'background.paper') : 'transparent',
              color: pricingSource === 'tcgdex' ? (theme.palette.mode === 'dark' ? 'primary.contrastText' : 'primary.main') : 'text.secondary',
              boxShadow: pricingSource === 'tcgdex' ? theme.shadows[1] : 'none',
              '&:hover': {
                bgcolor: pricingSource === 'tcgdex' ? (theme.palette.mode === 'dark' ? 'primary.dark' : 'background.paper') : alpha(theme.palette.action.hover, 0.04),
              }
            }}
          >
            TCGdex API
          </Button>
          <Button
            size="small"
            fullWidth
            onClick={() => setPricingSource('justtcg')}
            sx={{
              py: 0.75,
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '6px',
              textTransform: 'none',
              bgcolor: pricingSource === 'justtcg' ? (theme.palette.mode === 'dark' ? 'primary.main' : 'background.paper') : 'transparent',
              color: pricingSource === 'justtcg' ? (theme.palette.mode === 'dark' ? 'primary.contrastText' : 'primary.main') : 'text.secondary',
              boxShadow: pricingSource === 'justtcg' ? theme.shadows[1] : 'none',
              '&:hover': {
                bgcolor: pricingSource === 'justtcg' ? (theme.palette.mode === 'dark' ? 'primary.dark' : 'background.paper') : alpha(theme.palette.action.hover, 0.04),
              }
            }}
          >
            JustTCG API (v2)
          </Button>
        </Box>
      )}

      {/* Price Summary */}
      <Box sx={{ mb: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ py: 2 }}>
            {(error as Error).message || 'Failed to load pricing data'}
          </Typography>
        )}

        {!loading && !error && activePricing && (
          <Box>
            {/* Hero price + trend chips */}
            <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Primary Price */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                  {activePricing === justTcgData ? 'JustTCG Market Price' : 'TCGdex Market Price'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    ${activePricing.marketPrice?.toFixed(2) || '—'}
                  </Typography>
                  {formatChange(activePricing.priceChange7d) && (
                    <Chip
                      label={formatChange(activePricing.priceChange7d)!.text + ' 7d'}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: formatChange(activePricing.priceChange7d)!.color,
                        bgcolor: alpha(formatChange(activePricing.priceChange7d)!.color, 0.1),
                        border: 'none',
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* JustTCG Price Teaser (Only visible under TCGdex view if JustTCG data exists) */}
              {game === 'pokemon' && pricingSource === 'tcgdex' && (
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 1, 
                  bgcolor: alpha(theme.palette.text.primary, 0.02), 
                  border: `1px solid ${theme.palette.divider}`,
                  maxWidth: 'fit-content',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                  }
                }}
                onClick={() => setPricingSource('justtcg')}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                    JustTCG Market Price
                  </Typography>
                  {loadingV2 ? (
                    <CircularProgress size={16} />
                  ) : justTcgData ? (
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        ${justTcgData.marketPrice?.toFixed(2) || '—'}
                      </Typography>
                      {formatChange(justTcgData.priceChange7d) && (
                        <Chip
                          label={formatChange(justTcgData.priceChange7d)!.text + ' 7d'}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            color: formatChange(justTcgData.priceChange7d)!.color,
                            bgcolor: alpha(formatChange(justTcgData.priceChange7d)!.color, 0.1),
                            border: 'none',
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      No JustTCG pricing available
                    </Typography>
                  )}
                </Box>
              )}

              {activePricing.prices?.[0]?.lastUpdated && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Updated {new Date(
                    typeof activePricing.prices[0].lastUpdated === 'number' && activePricing.prices[0].lastUpdated < 10000000000 
                      ? activePricing.prices[0].lastUpdated * 1000 
                      : activePricing.prices[0].lastUpdated
                  ).toLocaleDateString()}
                </Typography>
              )}
            </Box>

            {/* Range stats — compact row */}
            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              mb: 2.5, 
              py: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}>
              {[
                { label: '7d Range', low: activePricing.minPrice7d, high: activePricing.maxPrice7d },
                { label: '30d Range', low: activePricing.minPrice30d, high: activePricing.maxPrice30d },
              ].map(({ label, low, high }) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    ${low?.toFixed(2) || '—'} – ${high?.toFixed(2) || '—'}
                  </Typography>
                </Box>
              ))}
              {/* Trend changes inline */}
              {[
                { label: '30d', val: activePricing.priceChange30d },
                { label: '90d', val: activePricing.priceChange90d },
              ].filter(t => t.val != null).map(({ label, val }) => {
                const f = formatChange(val);
                if (!f) return null;
                return (
                  <Box key={label}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {label} Change
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: f.color }}>
                      {f.text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Price History Chart */}
            <PriceHistoryChart pricingData={activePricing} />

            {/* Condition breakdown */}
            {activePricing.prices && activePricing.prices.length > 0 && (
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Condition</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Printing</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activePricing.prices.map((priceData, idx) => (
                      <TableRow key={`${priceData.condition}-${priceData.printing || 'normal'}-${idx}`}>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{priceData.condition}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{priceData.printing || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          ${priceData.price?.toFixed(2) || '0.00'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontSize: '0.65rem' }}>
              Data via {activePricing === justTcgData ? 'JustTCG' : 'TCGdex'}
            </Typography>
          </Box>
        )}

        {!loading && !error && !activePricing && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No pricing data available
          </Typography>
        )}
      </Box>

      {/* Marketplace tabs — pill-style buttons */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {marketTabs.map((label, i) => (
          <Chip
            key={label}
            label={label}
            onClick={() => setMarketTab(i)}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              cursor: 'pointer',
              bgcolor: marketTab === i ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
              color: marketTab === i ? theme.palette.primary.main : 'text.secondary',
              border: `1px solid ${marketTab === i ? alpha(theme.palette.primary.main, 0.3) : theme.palette.divider}`,
              '&:hover': {
                bgcolor: marketTab === i ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.action.hover, 0.08),
              },
            }}
          />
        ))}
      </Stack>

      {/* eBay */}
      {marketTab === 0 && (
        <EbayMarketplace
          cardNumber={cardNumber}
          cardName={name}
          setId={setId}
          searchQuery={ebaySearchQuery}
        />
      )}

      {/* TCGPlayer */}
      {marketTab === 1 && (
        <Button
          fullWidth
          variant="outlined"
          endIcon={<OpenInNewIcon sx={{ fontSize: '0.9rem !important' }} />}
          href={tcgplayerUrl}
          target="_blank"
          sx={{ 
            justifyContent: 'space-between', 
            py: 1.5,
            borderColor: theme.palette.divider,
            color: 'text.primary',
            '&:hover': { borderColor: theme.palette.primary.main },
          }}
        >
          View on TCGPlayer
        </Button>
      )}

      {/* PriceCharting */}
      {marketTab === 2 && (
        <Button
          fullWidth
          variant="outlined"
          endIcon={<OpenInNewIcon sx={{ fontSize: '0.9rem !important' }} />}
          href={priceChartUrl}
          target="_blank"
          sx={{ 
            justifyContent: 'space-between', 
            py: 1.5,
            borderColor: theme.palette.divider,
            color: 'text.primary',
            '&:hover': { borderColor: theme.palette.primary.main },
          }}
        >
          View on PriceCharting
        </Button>
      )}
    </Box>
  );
};

export default MarketPricing;
